/**
 * index.js — /calendar application shell.
 *
 * Owns: routing between views, the toolbar, quick-add, keyboard shortcuts,
 * reminders and the settings drawer. Data lives in store.js; drawing lives in
 * views.js; the entry form lives in editor.js. This file is the wiring.
 */

import { CalendarStore } from './store.js';
import { Editor } from './editor.js';
import { Habits } from './habits.js';
import { renderWeek, renderMonth, renderAgenda, renderTodo, renderNow } from './views.js';
import { parseQuickAdd } from './quickadd.js';
import { presetsFor, presetRows } from './presets.js';
import { holidaysOn, countryList, defaultCountry, regionList, defaultRegion, ready as holidaysReady, configure as configureHolidays } from './holidays.js';
import { el, clear, todayIso, isoAdd, isoDow, weekStartIso, fmtDate, weekdayNames, toast, announce, nowMinute, isIsoDate } from './ui.js';

const CFG = JSON.parse(document.getElementById('calCfg').textContent);
const I18N = CFG.i18n;
const LOCALE = CFG.locale;

/**
 * Habits are offered only where the server said so (CFG.habits): the habit
 * tracker ships hidden, and until it is public the tab must not appear in the
 * public calendar either — «built hidden» has to mean hidden everywhere.
 */
const HABITS_ON = !!CFG.habits;

/** ?view=habits (a push reminder's link, the progress page) or the page's own mode. */
function initialView() {
    let v = null;
    try { v = new URLSearchParams(window.location.search).get('view'); } catch (e) { v = null; }
    v = v || CFG.initialView || CFG.settings.default_view || 'week';
    if (v === 'habits' && !HABITS_ON) v = 'week';
    return ['month', 'week', 'day', 'agenda', 'todo', 'habits'].indexOf(v) >= 0 ? v : 'week';
}

const state = {
    view: initialView(),
    cursor: null,   // ISO date the current view is anchored on
    query: '',      // to-do search
};

/**
 * How many days the "week" view actually shows.
 *
 * Seven columns on a 390px phone is ~40px per day: the titles are unreadable
 * and tapping the right day is a coin flip. Every calendar people already use
 * (Google, Apple, Fantastical) drops to three days on a phone, and so do we.
 */
function weekDays() {
    const w = window.innerWidth || 1024;
    return w < 560 ? 3 : (w < 820 ? 5 : 7);
}

const store = new CalendarStore({
    authed: CFG.authed,
    csrf: CFG.csrf,
    timezone: CFG.settings.timezone,
    limit: CFG.limit,
});
Object.assign(store.settings, CFG.settings);

const dom = {
    now: document.getElementById('calNow'),
    grid: document.getElementById('calGrid'),
    range: document.getElementById('calRange'),
    views: document.getElementById('calViews'),
    quick: document.getElementById('calQuick'),
    hint: document.getElementById('calQuickHint'),
    settings: document.getElementById('calSettings'),
    guest: document.getElementById('calGuestNote'),
    offline: document.getElementById('calOffline'),
};

/**
 * Display-only preferences, kept in localStorage via the shared primitive.
 *
 * They describe how the grid is drawn, not what is in it, so they are not part
 * of the calendar's data: no column, no migration on either shard, and a guest
 * gets exactly the same behaviour as a signed-in owner.
 *
 * `holidays` is a country code, or '' for off. It defaults to the country most
 * of this locale's visitors live in, because a calendar that shows nothing on
 * 1 January is not neutral — it is wrong for almost everyone.
 */
const PREF = {
    get holidays() {
        const v = window.__toolSettings && window.__toolSettings.load('calendar', 'holidays', null);
        return v === null || v === undefined ? defaultCountry(LOCALE) : v;
    },
    set holidays(v) { window.__toolSettings && window.__toolSettings.save('calendar', 'holidays', v); },
    // Region of that country ('' = nationwide days only). Stored per country so switching
    // countries back and forth keeps each one's region; a country with a mandatory region
    // (GB: England and Wales / Scotland / Northern Ireland) starts on its default.
    get holidaysRegion() {
        const c = this.holidays;
        const v = window.__toolSettings && window.__toolSettings.load('calendar', 'holidays_region_' + c, null);
        return v === null || v === undefined ? defaultRegion(c) : v;
    },
    set holidaysRegion(v) { window.__toolSettings && window.__toolSettings.save('calendar', 'holidays_region_' + this.holidays, v); },
    get weekNums() {
        return !!(window.__toolSettings && window.__toolSettings.load('calendar', 'week_numbers', false));
    },
    set weekNums(v) { window.__toolSettings && window.__toolSettings.save('calendar', 'week_numbers', !!v); },
};

const ctx = {
    locale: LOCALE,
    i18n: I18N,
    // Whether anything reads this calendar for free time — see the blade.
    blocksBookings: !!CFG.blocksBookings,
    dowShort: weekdayNames(LOCALE, 1, 'short'),
    dowLong: weekdayNames(LOCALE, 1, 'long'),
    // Views ask through ctx so they never import the preference plumbing.
    // The array carries `.off` (a day off there) — see holidays.js.
    holidaysOn: (date) => holidaysOn(PREF.holidays, date, PREF.holidaysRegion),
    // «3 of 5 habits» for a day — null when there are no habits (or they are off).
    habitsOn: (date) => (HABITS_ON && habits ? habits.summary(date) : null),
    weekNums: () => PREF.weekNums,
    on: {
        open: (item, date) => editor.open(item, date),
        create: (seed) => editor.open(null, seed && seed.start_date, seed),
        toggle: (item, date) => toggleDone(item, date),
        // Курсор принимает только настоящую дату. Раньше сюда проходило любое
        // значение, и оно жило в состоянии до первого «вперёд», где ломалось
        // уже далеко отсюда — держим границу на входе.
        jump: (date, view) => {
            if (isIsoDate(date)) state.cursor = date;
            setView(view || state.view);
        },
        limitReached: () => toast(I18N.err_limit.replace(':cap', String(store.limit)), 'error'),
        rerender: () => render(),
        snooze: (item, date) => snooze(item, date),
        duplicate: (item, date) => duplicate(item, date),
        moveInbox: (id, dir) => moveInbox(id, dir),
        undoable: (label, revert) => offerUndo(label, revert),
        moveTime: (item, date, min) => moveTime(item, date, min),
        moveTo: (item, from, to, min) => moveTo(item, from, to, min),
        resize: (item, date, dur) => resize(item, date, dur),
        openHabits: (date) => {
            if (!HABITS_ON) return;
            if (habits && isIsoDate(date)) habits.jumpTo(date);
            setView('habits');
            funnel('habits_open', { from: 'grid' });
        },
        via: (v) => { nextVia = v; },
    },
};

let editor;
let habits = null;
// Declared up here, not beside syncQuickForView: boot() runs synchronously at
// its place in the module and reaches setView() before a later `let` exists.
let quickPreview = null;

/* ── telemetry ─────────────────────────────────────────────────────── */

/**
 * One wrapper instead of `window.__aeFunnel && window.__aeFunnel(...)` at
 * fifteen call sites, and one place that knows the shape of a calendar event.
 * The slug comes from <meta name="tool-slug">, so nothing is passed in.
 *
 * ⛔ Never call Timbrica3D.init() here — on a non-3D tool it silently does
 * nothing and the tool ends up with no telemetry at all (CLAUDE.md).
 */
function funnel(name, data) {
    try { if (window.__aeFunnel) window.__aeFunnel(name, data || {}); } catch (e) { /* telemetry must never break the tool */ }
}

function stage(name) {
    try { if (window.__stage) window.__stage(name); } catch (e) { /* ignore */ }
}

/**
 * The one event that says the tool did its job. For a calendar that is not a
 * downloaded file — it is the moment the calendar stops being empty, because a
 * calendar with entries is one the person comes back to.
 */
let completeSent = false;
/**
 * What the user was doing when the calendar stopped being empty. Set BEFORE the
 * action, because store.onChange fires synchronously inside create() — an
 * explicit maybeComplete('quick_add') after the await always lost the race and
 * every first entry was reported as a generic 'created'.
 */
let nextVia = 'created';

function maybeComplete(via) {
    if (completeSent || !store.count) return;
    completeSent = true;
    via = via || nextVia;
    stage('has_entries');
    try {
        if (window.__aeToolComplete) {
            window.__aeToolComplete({
                via, items: store.count, authed: store.authed ? 1 : 0,
                published: store.settings.visibility !== 'private' ? 1 : 0,
            });
        }
    } catch (e) { /* ignore */ }
}

/* ── boot ──────────────────────────────────────────────────────────── */

(async function boot() {
    state.cursor = todayIso(store.settings.timezone);
    editor = new Editor(store, ctx);
    if (HABITS_ON) habits = new Habits(store, ctx, funnel);

    store.onChange(render);
    buildToolbar();
    buildSettings();
    wireQuickAdd();
    wireKeys();
    wireKeysSheet();
    wireJump();
    wireSwipe();

    await store.load(CFG.bootstrap);

    if (!store.authed && dom.guest) dom.guest.hidden = false;
    render();
    // Holiday tables load per country (one small JSON); the grid is drawn first and gets its
    // labels a moment later — never blocked on them.
    configureHolidays(CFG.holidays);
    holidaysReady(PREF.holidays).then(() => { fillHolidaySettings(); render(); });
    startClock();
    startReminders();

    stage('ready');
    funnel('open', {
        view: state.view, items: store.count,
        habits: HABITS_ON ? store.habits().length : -1,
        authed: store.authed ? 1 : 0,
        published: store.settings.visibility !== 'private' ? 1 : 0,
        // Does this person actually keep a calendar here, or is it their first
        // visit? Two very different funnels wearing the same URL.
        returning: store.count > 0 ? 1 : 0,
    });
    if (store.count) maybeComplete('existing');
    // Registered AFTER the line above so entries that were already here on
    // load are reported as 'existing' and not as something created now.
    store.onChange(() => maybeComplete(null));

    // A queued write from a previous offline session goes out as soon as the
    // browser says it has a network again.
    window.addEventListener('online', () => store.flushQueue());
})();

/* ── render ────────────────────────────────────────────────────────── */

function render() {
    // The habits view has its own head (today's ring and «+ Привычка»);
    // the «now» card and the entry «+» above it would be a second, different
    // answer to «what about today» and a second add button.
    const habitsView = state.view === 'habits' && !!habits;
    if (dom.now) dom.now.hidden = habitsView;
    const addBtn = document.getElementById('calAdd');
    if (addBtn) addBtn.hidden = habitsView;
    renderNow(dom.now, store, ctx);
    renderRange();
    clear(dom.grid);

    if (state.view === 'habits' && habits) habits.render(dom.grid);
    else if (state.view === 'month') renderMonth(dom.grid, store, state, ctx);
    else if (state.view === 'week') renderWeek(dom.grid, store, state, ctx, weekDays());
    else if (state.view === 'day') renderWeek(dom.grid, store, state, ctx, 1);
    else if (state.view === 'agenda') renderAgenda(dom.grid, store, state, ctx, 60);
    else renderTodo(dom.grid, store, state, ctx);

    if (dom.offline) dom.offline.hidden = !store.dirty;
    syncSettingsUi();
}

function renderRange() {
    const ws = store.settings.week_start || 1;
    let text;
    if (state.view === 'habits' && habits) text = habits.rangeLabel();
    else if (state.view === 'month') text = fmtDate(state.cursor, LOCALE, { month: 'long', year: 'numeric' });
    else if (state.view === 'day') text = fmtDate(state.cursor, LOCALE, { weekday: 'long', day: 'numeric', month: 'long' });
    else if (state.view === 'week') {
        const n = weekDays();
        // On a narrow screen the view starts at the CURSOR, not at Monday, so
        // the label has to say the same thing the columns do.
        const a = n === 7 ? weekStartIso(state.cursor, ws) : state.cursor;
        const b = isoAdd(a, n - 1);
        text = fmtDate(a, LOCALE, { day: 'numeric', month: 'short' }) + ' — ' + fmtDate(b, LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });
    } else text = I18N['view_' + state.view];
    dom.range.textContent = text;

    // The to-do list is the one view with no date cursor to move: its label
    // reads «Дела», and a date picker behind that word answers a question
    // nobody asked. The caret goes with the behaviour, so the button never
    // advertises something it will not do.
    const jumpable = state.view !== 'todo';
    dom.range.classList.toggle('is-plain', !jumpable);
    dom.range.setAttribute('aria-haspopup', jumpable ? 'dialog' : 'false');
}

function setView(v) {
    if (v === 'habits' && !HABITS_ON) v = 'week';
    state.view = v;
    syncQuickForView();
    dom.views.querySelectorAll('[data-view]').forEach((b) => {
        const on = b.dataset.view === v;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    render();
    stage('view_' + v);
}

function step(dir) {
    const ws = store.settings.week_start || 1;
    if (state.view === 'habits' && habits) {
        habits.shift(dir);
        render();
        announce(dom.range.textContent);
        return;
    }
    if (state.view === 'month') {
        const [y, m] = state.cursor.split('-').map(Number);
        const nd = new Date(Date.UTC(y, m - 1 + dir, 1));
        state.cursor = nd.toISOString().slice(0, 10);
    } else if (state.view === 'day') state.cursor = isoAdd(state.cursor, dir);
    else if (state.view === 'week') {
        const n = weekDays();
        state.cursor = n === 7
            ? isoAdd(weekStartIso(state.cursor, ws), dir * 7)
            : isoAdd(state.cursor, dir * n);
    }
    else state.cursor = isoAdd(state.cursor, dir * 7);
    render();
    announce(dom.range.textContent);
}

/* ── toolbar ───────────────────────────────────────────────────────── */

function buildToolbar() {
    document.getElementById('calPrev').addEventListener('click', () => step(-1));
    document.getElementById('calNext').addEventListener('click', () => step(1));
    document.getElementById('calToday').addEventListener('click', () => {
        state.cursor = todayIso(store.settings.timezone);
        if (habits) habits.end = null;
        render();
    });
    // In the habits view «+» makes a habit — the button the eye is already on.
    document.getElementById('calAdd').addEventListener('click', () => {
        if (state.view === 'habits' && habits) { habits.openEditor(null); return; }
        ctx.on.create({ start_date: state.cursor });
    });

    dom.views.querySelectorAll('[data-view]').forEach((b) => {
        b.addEventListener('click', () => setView(b.dataset.view));
    });
    setView(state.view);
}

/* ── jump to a date ────────────────────────────────────────────────── */

/**
 * The month grid behind the range label.
 *
 * ‹ › answers "the week after this one" and nothing else: reaching the 14th of
 * next March costs twelve clicks and twelve full re-renders. Every calendar
 * people already use puts a month grid one tap from the range label, so here
 * the label IS the button.
 *
 * `jumpMonth` is the month the PICKER is showing and deliberately NOT
 * `state.cursor` — you can page through to December and change your mind
 * without the calendar behind the dialog having moved at all.
 */
let jumpMonth = null;
let jumpMode = 'days';       // 'days' | 'months'

function wireJump() {
    const dlg = document.getElementById('calJump');
    if (!dlg || !dom.range) return;

    dom.range.addEventListener('click', () => {
        if (dom.range.classList.contains('is-plain')) return;
        jumpMonth = state.cursor.slice(0, 8) + '01';
        jumpMode = 'days';
        renderJump();
        try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
        funnel('jump_open', { view: state.view });
    });
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => dlg.close()));

    const today = dlg.querySelector('[data-jump-today]');
    if (today) {
        today.addEventListener('click', () => {
            state.cursor = todayIso(store.settings.timezone);
            dlg.close();
            render();
            announce(dom.range.textContent);
        });
    }
}

function shiftMonth(iso, n) {
    const [y, m] = iso.split('-').map(Number);
    const nd = new Date(Date.UTC(y, m - 1 + n, 1));
    return nd.toISOString().slice(0, 10);
}

function renderJump() {
    const host = document.getElementById('calJumpBody');
    if (!host) return;
    clear(host);

    const ws = store.settings.week_start || 1;
    const today = todayIso(store.settings.timezone);
    const months = jumpMode === 'months';

    const nav = el('div', { class: 'cal-jump__nav' });
    nav.appendChild(el('button', {
        type: 'button', class: 'cal-btn cal-btn--icon cal-prev', 'aria-label': I18N.prev, text: '‹',
        onclick: () => { jumpMonth = shiftMonth(jumpMonth, months ? -12 : -1); renderJump(); },
    }));
    // The heading toggles between the day grid and a year of months — one more
    // tap gets you a year away, which is the distance ‹ › is worst at.
    nav.appendChild(el('button', {
        type: 'button', class: 'cal-jump__label',
        text: months ? jumpMonth.slice(0, 4) : fmtDate(jumpMonth, LOCALE, { month: 'long', year: 'numeric' }),
        'aria-expanded': months ? 'true' : 'false',
        onclick: () => { jumpMode = months ? 'days' : 'months'; renderJump(); },
    }));
    nav.appendChild(el('button', {
        type: 'button', class: 'cal-btn cal-btn--icon cal-next', 'aria-label': I18N.next_period, text: '›',
        onclick: () => { jumpMonth = shiftMonth(jumpMonth, months ? 12 : 1); renderJump(); },
    }));
    host.appendChild(nav);

    if (months) {
        const grid = el('div', { class: 'cal-jump__months' });
        for (let m = 0; m < 12; m++) {
            const iso = jumpMonth.slice(0, 4) + '-' + String(m + 1).padStart(2, '0') + '-01';
            const on = iso.slice(0, 7) === state.cursor.slice(0, 7);
            grid.appendChild(el('button', {
                type: 'button', class: 'cal-seg__b' + (on ? ' is-on' : ''),
                text: fmtDate(iso, LOCALE, { month: 'short' }),
                onclick: () => { jumpMonth = iso; jumpMode = 'days'; renderJump(); },
            }));
        }
        host.appendChild(grid);
        return;
    }

    const grid = el('div', { class: 'cal-jump__grid' });
    weekdayNames(LOCALE, ws, 'short').forEach((n) => grid.appendChild(el('div', { class: 'cal-jump__dow', text: n })));

    // Exactly as many rows as this month needs. A fixed 42 leaves a whole
    // blank row under most months and makes the sheet taller than its content.
    const [y, mo] = jumpMonth.split('-').map(Number);
    const inMonthDays = new Date(Date.UTC(y, mo, 0)).getUTCDate();
    const offset = (isoDow(jumpMonth) - ws + 7) % 7;
    const cells = Math.ceil((offset + inMonthDays) / 7) * 7;
    const start = weekStartIso(jumpMonth, ws);
    const month = jumpMonth.slice(0, 7);

    for (let i = 0; i < cells; i++) {
        const d = isoAdd(start, i);
        const has = store.onDate(d).length > 0;
        grid.appendChild(el('button', {
            type: 'button',
            class: 'cal-jump__d'
                + (d.slice(0, 7) === month ? '' : ' is-out')
                + (d === today ? ' is-today' : '')
                + (d === state.cursor ? ' is-cursor' : '')
                + (has ? ' has-items' : ''),
            text: String(Number(d.slice(8))),
            'aria-label': fmtDate(d, LOCALE, { day: 'numeric', month: 'long', year: 'numeric' }),
            'aria-current': d === today ? 'date' : null,
            'data-date': d,
            onclick: () => {
                state.cursor = d;
                if (state.view === 'habits' && habits) habits.jumpTo(d);
                const dlg = document.getElementById('calJump');
                if (dlg) dlg.close();
                render();
                announce(dom.range.textContent);
                funnel('jump_pick', { view: state.view });
            },
        }));
    }
    host.appendChild(grid);
}

/* ── swipe ─────────────────────────────────────────────────────────── */

/**
 * Sideways swipe = previous / next period.
 *
 * Deliberately narrow. It ignores a mouse, ignores gestures that begin on a
 * block or a control (those own their own drag), and demands a clearly
 * horizontal movement — anything looser starts eating the vertical scroll of
 * the hour grid, which is the one gesture a week view cannot afford to lose.
 */
function wireSwipe() {
    if (!dom.grid) return;
    let x0 = 0;
    let y0 = 0;
    let live = false;

    dom.grid.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        if (e.target.closest && e.target.closest('.cal-block,.cal-chip,button,input,select,textarea,a')) return;
        x0 = e.clientX; y0 = e.clientY; live = true;
    });
    dom.grid.addEventListener('pointerup', (e) => {
        if (!live) return;
        live = false;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.8) return;
        // In an RTL layout the columns run the other way, so the same finger
        // movement has to mean the other direction.
        const rtl = document.documentElement.dir === 'rtl';
        step((dx < 0) === !rtl ? 1 : -1);
        funnel('swipe_nav', { view: state.view });
    });
    dom.grid.addEventListener('pointercancel', () => { live = false; });
}

/* ── quick add ─────────────────────────────────────────────────────── */

function wireQuickAdd() {
    if (!dom.quick) return;
    // "Enter — добавить, Shift+Enter — открыть форму" is instructions for a
    // keyboard nobody holding a phone has.
    const touch = !!(window.matchMedia && window.matchMedia('(hover: none)').matches);
    const idleHint = touch ? (I18N.quick_hint_touch || I18N.quick_hint) : I18N.quick_hint;
    const preview = () => {
        if (state.view === 'habits' && habits) {
            const tH = I18N.habits || {};
            if (!dom.quick.value.trim()) { dom.hint.textContent = tH.quick_hint || idleHint; dom.hint.classList.remove('is-live'); return; }
            dom.hint.textContent = habits.previewLine(dom.quick.value);
            dom.hint.classList.add('is-live');
            return;
        }
        const p = parseQuickAdd(dom.quick.value, { locale: LOCALE, timezone: store.settings.timezone });
        if (!dom.quick.value.trim()) { dom.hint.textContent = idleHint; dom.hint.classList.remove('is-live'); return; }
        const bits = [];
        if (p.start_date) bits.push(fmtDate(p.start_date, LOCALE, { day: 'numeric', month: 'short' }));
        if (p.start_min != null) bits.push(String(Math.floor(p.start_min / 60)).padStart(2, '0') + ':' + String(p.start_min % 60).padStart(2, '0'));
        if (p.duration_min) bits.push(p.duration_min + I18N.min_short);
        if (p.rrule) bits.push('↻');
        dom.hint.textContent = bits.length ? bits.join(' · ') + ' — ' + (p.title || '…') : idleHint;
        dom.hint.classList.toggle('is-live', bits.length > 0);
    };

    dom.quick.addEventListener('input', preview);
    quickPreview = preview;
    dom.quick.addEventListener('keydown', async (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const raw = dom.quick.value.trim();
        if (!raw) return;
        if (state.view === 'habits' && habits) {
            nextVia = 'habit_quick';
            try {
                const d = await habits.quickAdd(raw);
                dom.quick.value = '';
                preview();
                announce(I18N.added.replace(':t', d.title));
            } catch (err) { ctx.on.limitReached(); }
            return;
        }
        const p = parseQuickAdd(raw, { locale: LOCALE, timezone: store.settings.timezone });
        if (!p.title) p.title = raw;
        nextVia = 'quick_add';

        // Shift+Enter opens the full form instead of saving — the escape hatch
        // when the sentence did not parse the way the hint shows.
        if (e.shiftKey) { editor.open(null, p.start_date, p); dom.quick.value = ''; preview(); return; }

        try {
            await store.create(p);
            dom.quick.value = '';
            preview();
            announce(I18N.added.replace(':t', p.title));
            funnel('quick_add', {
                parsed_time: p.start_min != null ? 1 : 0,
                parsed_date: p.start_date ? 1 : 0,
                repeat: p.rrule ? 1 : 0,
                // Did the sentence parser earn its keep, or did the whole line
                // just become a title? That ratio is the only honest measure of
                // whether the feature works in a given locale.
                understood: (p.start_date || p.start_min != null || p.rrule) ? 1 : 0,
            });
        } catch (err) { ctx.on.limitReached(); }
    });
    preview();
}

/** The quick-add line speaks for the current view: entries, or habits. */
function syncQuickForView() {
    if (!dom.quick) return;
    const tH = I18N.habits || {};
    const habitsView = state.view === 'habits' && !!habits;
    if (!dom.quick.dataset.phEntries) dom.quick.dataset.phEntries = dom.quick.getAttribute('placeholder') || '';
    dom.quick.setAttribute('placeholder', habitsView ? (tH.quick_ph || dom.quick.dataset.phEntries) : dom.quick.dataset.phEntries);
    const lbl = document.querySelector('label[for="calQuick"]');
    if (lbl) lbl.textContent = dom.quick.getAttribute('placeholder');
    if (quickPreview) quickPreview();
}

/* ── ticking ───────────────────────────────────────────────────────── */

async function toggleDone(item, date) {
    if (!date) {
        await store.update(item.id, { status: item.status === 'done' ? 'open' : 'done' });
        return;
    }
    const occ = store.occurrenceOf(item.id, date);
    const done = item.rrule ? !!(occ && occ.status === 'done') : item.status === 'done';
    await store.setOccurrence(item.id, date, done ? 'open' : 'done', occ ? occ.moved_min : null);
    announce(done ? I18N.mark_undone : I18N.mark_done);
    funnel('tick', { repeat: item.rrule ? 1 : 0, undone: done ? 1 : 0 });
}

/* ── undo ──────────────────────────────────────────────────────────── */

/**
 * A strip that offers to take the last destructive action back.
 *
 * Deliberately NOT a confirmation dialog: a confirm interrupts every deletion
 * that WAS meant, and still cannot rescue the one that wasn't — you have already
 * clicked "yes" by reflex. Undo costs nothing when you were right and everything
 * back when you were wrong.
 */
let undoTimer = null;

function offerUndo(label, revert) {
    const bar = document.getElementById('calUndo');
    if (!bar) return;
    clear(bar);
    bar.appendChild(el('span', { class: 'cal-undo__t', text: label }));
    bar.appendChild(el('button', {
        type: 'button', class: 'cal-undo__btn', text: I18N.undo,
        onclick: async () => {
            hideUndo();
            try { await revert(); toast(I18N.undone, 'success'); funnel('undo', {}); }
            catch (e) { toast(I18N.save_error, 'error'); }
        },
    }));
    bar.appendChild(el('button', {
        type: 'button', class: 'cal-undo__x', 'aria-label': I18N.close, text: '✕', onclick: hideUndo,
    }));
    bar.hidden = false;
    announce(label);
    clearTimeout(undoTimer);
    undoTimer = setTimeout(hideUndo, 12000);
}

function hideUndo() {
    const bar = document.getElementById('calUndo');
    clearTimeout(undoTimer);
    if (bar) { bar.hidden = true; clear(bar); }
}

/* ── row actions ───────────────────────────────────────────────────── */

/**
 * «Отложить» — the single most-used verb in any to-do list. Moves the entry to
 * tomorrow; on a repeating one it lifts THIS day out (EXDATE) and drops a
 * one-off copy on tomorrow, so the habit itself keeps its rhythm.
 */
async function snooze(item, dateIso) {
    // An undated task has nothing to postpone FROM, so tomorrow is measured
    // from today. Same gesture, same result: it lands on tomorrow.
    const to = isoAdd(dateIso || todayIso(store.settings.timezone), 1);
    if (item.rrule) {
        await store.update(item.id, { exdates: (item.exdates || []).concat([dateIso]) });
        const copy = Object.assign({}, item, {
            start_date: to, rrule: null, rrule_until: null, rrule_count: null, exdates: [],
        });
        delete copy.id; delete copy.uid;
        try { await store.create(copy); } catch (e) { ctx.on.limitReached(); return; }
    } else {
        await store.update(item.id, { start_date: to });
    }
    toast(I18N.snoozed.replace(':d', fmtDate(to, LOCALE, { day: 'numeric', month: 'short' })), 'success');
    funnel('snooze', { repeat: item.rrule ? 1 : 0, undated: dateIso ? 0 : 1 });
}

/**
 * A block was dragged to a new time.
 *
 * On a REPEATING entry this moves that day alone — which is exactly what the
 * per-day row (`moved_min`) is for, and what a person dragging one Tuesday
 * means. Moving the whole series is a deliberate act and lives in the editor.
 */
async function moveTime(item, dateIso, min) {
    if (item.rrule && dateIso) {
        const occ = store.occurrenceOf(item.id, dateIso);
        const wasMin = occ ? occ.moved_min : null;
        await store.setOccurrence(item.id, dateIso, (occ && occ.status) || 'open', min);
        offerUndo(I18N.moved.replace(':t', item.title),
            () => store.setOccurrence(item.id, dateIso, (occ && occ.status) || 'open', wasMin));
    } else {
        const was = item.start_min;
        await store.update(item.id, { start_min: min });
        offerUndo(I18N.moved.replace(':t', item.title), () => store.update(item.id, { start_min: was }));
    }
    funnel('drag_move', { repeat: item.rrule ? 1 : 0 });
}

/**
 * A block was dragged onto a DIFFERENT day.
 *
 * On a repeating entry this takes the same shape «отложить» already takes, and
 * for the same reason: the day is lifted out of the series (EXDATE) and a
 * standalone copy lands on the new date, so the rhythm of the habit survives a
 * single Thursday being moved. Both halves are undone together — a half-undo
 * that put the day back AND left the copy would quietly duplicate the entry.
 */
async function moveTo(item, fromIso, toIso, min) {
    const label = I18N.moved.replace(':t', item.title);

    if (item.rrule) {
        const id = item.id;
        const before = (item.exdates || []).slice();
        await store.update(id, { exdates: before.concat([fromIso]) });
        const copy = Object.assign({}, item, {
            start_date: toIso, start_min: min,
            rrule: null, rrule_until: null, rrule_count: null, exdates: [],
        });
        delete copy.id; delete copy.uid;
        let made = null;
        try {
            made = await store.create(copy);
        } catch (e) {
            // The copy is the whole point of the move. If it cannot be made,
            // put the day back rather than leaving a hole in the series.
            await store.update(id, { exdates: before });
            ctx.on.limitReached();
            return;
        }
        offerUndo(label, async () => {
            if (made && made.id != null) await store.remove(made.id);
            await store.update(id, { exdates: before });
        });
    } else {
        const was = { start_date: item.start_date, start_min: item.start_min };
        await store.update(item.id, { start_date: toIso, start_min: min });
        offerUndo(label, () => store.update(item.id, was));
    }
    funnel('drag_move', { repeat: item.rrule ? 1 : 0, cross_day: 1 });
}

/**
 * A block's bottom edge was dragged — it now lasts a different length of time.
 *
 * Only ever reached for a non-repeating entry (views.js does not fit the grip
 * to a series), so this is an ordinary field change with an undo behind it.
 */
async function resize(item, dateIso, durationMin) {
    const was = item.duration_min;
    await store.update(item.id, { duration_min: durationMin });
    offerUndo(I18N.resized.replace(':t', item.title), () => store.update(item.id, { duration_min: was }));
    funnel('drag_resize', { minutes: durationMin });
}

async function duplicate(item, dateIso) {
    const copy = Object.assign({}, item, { start_date: dateIso || item.start_date, exdates: [] });
    delete copy.id; delete copy.uid;
    copy.title = copy.title.slice(0, 190);
    try {
        await store.create(copy);
        toast(I18N.duplicated, 'success');
        funnel('duplicate', {});
    } catch (e) { ctx.on.limitReached(); }
}

/** Manual order inside «Когда-нибудь» — the only list with an order to keep. */
async function moveInbox(id, dir) {
    const list = store.inbox().filter((i) => i.status === 'open');
    const at = list.findIndex((i) => String(i.id) === String(id));
    const to = at + dir;
    if (at < 0 || to < 0 || to >= list.length) return;
    const ids = list.map((i) => i.id);
    ids.splice(to, 0, ids.splice(at, 1)[0]);
    await store.reorder(ids);
    render();
    announce(I18N['move_' + (dir < 0 ? 'up' : 'down')]);
}

/* ── settings drawer ───────────────────────────────────────────────── */

/**
 * Country list, region list and the holiday-subscription link — rebuilt whenever the index
 * or a country arrives (the lists come from the holiday layer, loaded after first paint).
 */
function fillHolidaySettings() {
    const dlg = dom.settings;
    if (!dlg) return;
    const hol = dlg.querySelector('[name=holidays]');
    const list = countryList();
    if (hol && (hol.options.length <= 1 || hol.options.length !== list.length + 1)) {
        clear(hol);
        hol.appendChild(el('option', { value: '', text: I18N.holidays_off }));
        list.forEach((c) => hol.appendChild(el('option', { value: c.code, text: c.label })));
    }
    if (hol) hol.value = PREF.holidays;
    const regs = PREF.holidays ? regionList(PREF.holidays) : [];
    const regF = dlg.querySelector('[data-holidays-region]');
    const reg = dlg.querySelector('[name=holidays_region]');
    if (regF) regF.hidden = !regs.length;
    if (reg) {
        clear(reg);
        reg.appendChild(el('option', { value: '', text: I18N.holidays_region_all || '—' }));
        regs.forEach((r) => reg.appendChild(el('option', { value: r.code, text: r.name })));
        reg.value = PREF.holidaysRegion;
        if (reg.value !== PREF.holidaysRegion) reg.value = '';
    }
    const feed = dlg.querySelector('[data-holidays-feed]');
    if (feed) {
        const cc = PREF.holidays;
        feed.hidden = !cc || !list.some((c) => c.code === cc);
        if (cc) {
            const r = PREF.holidaysRegion;
            // site URLs are lowercase (upper case gets a 301)
            feed.href = '/' + LOCALE + '/calendar-feed/' + cc.toLowerCase() + (r ? '/' + encodeURIComponent(r.toLowerCase()) : '');
        }
    }
}

function buildSettings() {
    const open = document.getElementById('calSettingsBtn');
    const dlg = dom.settings;
    if (!open || !dlg) return;
    open.addEventListener('click', () => { try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); } });
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => dlg.close()));

    dlg.querySelectorAll('[name=visibility]').forEach((r) => {
        r.addEventListener('change', async () => {
            const res = await store.saveSettings({ visibility: r.value });
            if (res && res.guest) { toast(I18N.need_account, 'warning'); syncSettingsUi(); return; }
            if (res && !res.ok) { toast(res.message || I18N.save_error, 'error'); await store.reload(); render(); return; }
            toast(r.value === 'private' ? I18N.now_private : I18N.now_published, 'success');
            funnel('visibility', { to: r.value, items: store.count });
        });
    });

    const tasks = dlg.querySelector('[name=show_tasks]');
    if (tasks) tasks.addEventListener('change', () => store.saveSettings({ show_tasks: tasks.checked }));

    const ws = dlg.querySelector('[name=week_start]');
    if (ws) ws.addEventListener('change', () => { store.saveSettings({ week_start: +ws.value }); render(); });

    const tz = dlg.querySelector('[name=timezone]');
    if (tz) tz.addEventListener('change', () => { store.saveSettings({ timezone: tz.value }); render(); });

    // Country and region lists come from the holiday layer's index — one source of truth.
    const hol = dlg.querySelector('[name=holidays]');
    if (hol) {
        hol.addEventListener('change', () => {
            PREF.holidays = hol.value;
            funnel('holidays', { country: hol.value || 'off' });
            fillHolidaySettings();
            holidaysReady(hol.value).then(() => { fillHolidaySettings(); render(); });
            render();
        });
    }
    const reg = dlg.querySelector('[name=holidays_region]');
    if (reg) {
        reg.addEventListener('change', () => {
            PREF.holidaysRegion = reg.value;
            funnel('holidays_region', { country: PREF.holidays, region: reg.value || 'all' });
            fillHolidaySettings();
            render();
        });
    }
    fillHolidaySettings();

    const wn = dlg.querySelector('[name=week_numbers]');
    if (wn) {
        wn.checked = PREF.weekNums;
        wn.addEventListener('change', () => { PREF.weekNums = wn.checked; render(); });
    }

    const headline = dlg.querySelector('[name=headline]');
    if (headline) headline.addEventListener('change', () => store.saveSettings({ headline: headline.value || null }));

    // Copy buttons for the two links (public page + subscription feed).
    dlg.querySelectorAll('[data-copy]').forEach((b) => {
        b.addEventListener('click', async () => {
            const src = dlg.querySelector(b.dataset.copy);
            if (!src) return;
            try { await navigator.clipboard.writeText(src.value || src.textContent); toast(I18N.copied, 'success'); }
            catch (e) { src.select && src.select(); }
        });
    });

    // Presets
    const host = dlg.querySelector('[data-presets]');
    if (host) {
        presetsFor(LOCALE).forEach((p) => {
            const label = (I18N.presets && I18N.presets[p.key] && I18N.presets[p.key].name) || p.key;
            host.appendChild(el('button', {
                type: 'button', class: 'cal-btn cal-btn--sm', text: label,
                onclick: async () => {
                    nextVia = 'preset';
                    const monday = weekStartIso(todayIso(store.settings.timezone), 1);
                    const rows = presetRows(p, monday, I18N);
                    for (const row of rows) {
                        if (store.atLimit()) break;
                        await store.create(row);
                    }
                    dlg.close();
                    setView('week');
                    toast(I18N.preset_added.replace(':n', String(rows.length)), 'success');
                    funnel('preset', { key: p.key, n: rows.length });
                },
            }));
        });
    }

    // Export .ics — built HERE, for everyone. The server route needs an account,
    // and a guest whose calendar cannot leave this browser is a guest whose work
    // is stranded on one device.
    const exportBtn = dlg.querySelector('[data-export]');
    if (exportBtn) {
        exportBtn.addEventListener('click', async () => {
            const { buildIcs } = await import('./ics.js');
            const text = buildIcs(store.items, { timezone: store.settings.timezone, name: I18N.title, months: 12 });
            const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
            // __downloadFile, not <a download> — the latter is a silent no-op in
            // Android WebView (see reference_download_primitive).
            if (window.__downloadFile) window.__downloadFile(blob, 'timbrica-calendar.ics');
            else {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob); a.download = 'timbrica-calendar.ics'; a.click();
            }
            funnel('export', { items: store.items.length });
        });
    }

    // Import .ics
    const file = dlg.querySelector('[name=ics]');
    if (file) {
        file.addEventListener('change', async () => {
            const f = file.files && file.files[0];
            if (!f) return;
            if (!store.authed) { toast(I18N.need_account, 'warning'); file.value = ''; return; }
            const text = await f.text();
            const r = await store.post('/api/calendar/import', { ics: text }).catch(() => null);
            funnel('import', { ok: r && r.ok ? 1 : 0, n: (r && r.imported) || 0 });
            file.value = '';
            if (r && r.ok) {
                store.absorb(r);
                render();
                toast(I18N.imported.replace(':n', String(r.imported)), 'success');
            } else {
                toast(I18N.import_error, 'error');
            }
        });
    }
}

function syncSettingsUi() {
    const dlg = dom.settings;
    if (!dlg) return;
    dlg.querySelectorAll('[name=visibility]').forEach((r) => { r.checked = r.value === store.settings.visibility; });
    const tasks = dlg.querySelector('[name=show_tasks]');
    if (tasks) tasks.checked = !!store.settings.show_tasks;
    const ws = dlg.querySelector('[name=week_start]');
    if (ws) ws.value = String(store.settings.week_start || 1);
    const pub = dlg.querySelector('[data-public-links]');
    if (pub) pub.hidden = store.settings.visibility === 'private';
    const state = dlg.querySelector('[data-vis-state]');
    if (state) state.textContent = I18N['vis_state_' + store.settings.visibility] || '';
}

/**
 * Push reminders — the ones that survive a closed tab.
 *
 * The transport is the site's existing stack (window.__timbricaPush: VAPID key,
 * pushManager.subscribe, /api/push/subscribe). Nothing here re-implements it;
 * the calendar only owns the switch and the honest label next to it.
 */
async function wirePush() {
    const btn = document.querySelector('[data-push-toggle]');
    const label = document.querySelector('[data-push-state]');
    const api = window.__timbricaPush;
    if (!btn || !label) return;

    if (!api || !api.supported) {
        // Said out loud rather than hidden: a missing switch reads as "we forgot",
        // a stated limitation reads as "we checked".
        label.textContent = I18N.push_unsupported;
        return;
    }

    const paint = async () => {
        const st = await api.state();
        btn.hidden = st === 'blocked';
        btn.textContent = st === 'subscribed' ? I18N.push_off : I18N.push_on;
        label.textContent = st === 'subscribed' ? I18N.push_enabled
            : st === 'blocked' ? I18N.push_denied : I18N.push_disabled;
    };

    btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
            const st = await api.state();
            if (st === 'subscribed') { await api.unsubscribe(); funnel('push', { to: 'off' }); }
            else { await api.subscribe(); funnel('push', { to: 'on' }); toast(I18N.push_enabled, 'success'); }
        } catch (e) {
            toast(String(e && e.message) === 'denied' ? I18N.push_denied : I18N.save_error, 'error');
        }
        btn.disabled = false;
        paint();
    });

    paint();
}

/* ── keyboard ──────────────────────────────────────────────────────── */

function wireKeys() {
    document.addEventListener('keydown', (e) => {
        const tag = (e.target && e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.metaKey || e.ctrlKey || e.altKey) return;
        if (document.querySelector('dialog[open]')) return;

        const map = { m: 'month', w: 'week', d: 'day', a: 'agenda', l: 'todo' };
        if (HABITS_ON) map.h = 'habits';
        if (map[e.key]) { setView(map[e.key]); e.preventDefault(); return; }
        if (e.key === 't') { state.cursor = todayIso(store.settings.timezone); render(); e.preventDefault(); return; }
        if (e.key === 'n' || e.key === '+') { ctx.on.create({ start_date: state.cursor }); e.preventDefault(); return; }
        if (e.key === 'ArrowLeft') { step(-1); e.preventDefault(); return; }
        if (e.key === 'ArrowRight') { step(1); e.preventDefault(); return; }
        if (e.key === '/') { dom.quick && dom.quick.focus(); e.preventDefault(); return; }
        if (e.key === '?') { openKeys(); e.preventDefault(); }
    });
}

/**
 * The shortcut sheet. These bindings have always worked — nothing advertised
 * them, which is the same place Google's users end up for the opposite reason
 * (theirs are off until you find the setting). On "?" or the toolbar button.
 */
function openKeys() {
    const dlg = document.getElementById('calKeys');
    if (!dlg) return;
    funnel('keys_sheet', {});
    try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
}

function wireKeysSheet() {
    const dlg = document.getElementById('calKeys');
    const btn = document.getElementById('calKeysBtn');
    if (btn) btn.addEventListener('click', openKeys);
    if (dlg) {
        dlg.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => dlg.close()));
        // Clicking the backdrop closes it, same as the other dialogs here.
        dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    }
}

/* ── live clock + reminders ────────────────────────────────────────── */

let lastMinute = -1;

function startClock() {
    setInterval(() => {
        const m = nowMinute(store.settings.timezone);
        if (m === lastMinute) return;
        lastMinute = m;
        // The "now" card and the now-line are the only time-dependent pieces —
        // re-rendering the whole grid every minute would fight the user's scroll.
        renderNow(dom.now, store, ctx);
        const line = dom.grid.querySelector('.cal-week__now');
        if (line) line.style.top = (m / 60 * 46) + 'px';
    }, 20000);
}

/**
 * Reminders fire from the OPEN TAB only, and say so in the settings drawer.
 * A promise of a notification we cannot keep once the tab is closed would be a
 * worse feature than no reminder at all — the subscription feed is the honest
 * answer for reminders that must survive the browser.
 */
const fired = new Set();

function startReminders() {
    setInterval(() => {
        const today = todayIso(store.settings.timezone);
        const min = nowMinute(store.settings.timezone);
        store.onDate(today).forEach((o) => {
            const lead = o.item.reminder_min;
            if (lead == null || o.min == null || o.done) return;
            const at = o.min - lead;
            if (min < at || min > at + 1) return;
            const key = o.item.id + '|' + today + '|' + at;
            if (fired.has(key)) return;
            fired.add(key);
            const body = lead === 0 ? I18N.rem_now : I18N.rem_soon.replace(':n', String(lead));
            if (window.Notification && Notification.permission === 'granted') {
                try { new Notification(o.item.title, { body, tag: key }); } catch (e) { toast(o.item.title + ' — ' + body, 'info'); }
            } else {
                toast(o.item.title + ' — ' + body, 'info');
            }
        });
    }, 30000);

    wirePush();

    const btn = document.getElementById('calNotifyBtn');
    if (btn && window.Notification) {
        const sync = () => { btn.hidden = Notification.permission === 'granted'; };
        btn.addEventListener('click', async () => {
            try { await Notification.requestPermission(); } catch (e) { /* Safari <16 */ }
            sync();
        });
        sync();
    } else if (btn) btn.hidden = true;
}
